import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { useSSEStream } from '../../../hooks/useSSEStream';
import type { AgentChatStreamEvent } from '../types';
import { CHAT_KEYS } from './useAgentChat';

interface UseAgentChatStreamOptions {
  organizationId: string;
  enabled?: boolean;
  onMessage?: (event: AgentChatStreamEvent) => void;
  getAccessToken?: () => Promise<string | null>;
}

export function useAgentChatStream({
  organizationId,
  enabled = true,
  onMessage,
  getAccessToken,
}: UseAgentChatStreamOptions) {
  const queryClient = useQueryClient();
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const handleMessage = useCallback(
    (data: string) => {
      try {
        const parsed: AgentChatStreamEvent = JSON.parse(data);
        onMessageRef.current?.(parsed);

        queryClient.invalidateQueries({
          queryKey: CHAT_KEYS.messages(organizationId, parsed.agentType),
        });
      } catch {
        // skip malformed events
      }
    },
    [queryClient, organizationId],
  );

  const { isConnected } = useSSEStream({
    path: 'agents/chat/stream',
    organizationId,
    enabled,
    getAccessToken,
    onMessage: handleMessage,
  });

  return { isConnected };
}
